import * as React from 'react';
import { find, flow, get, head, isEmpty } from 'lodash';
import { formValueSelector, getFormSyncErrors, InjectedFormProps, reduxForm, submit } from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { Form } from 'react-bootstrap';
import { IAuthState } from '../../../../services/auth/models';
import {
  IChannel,
  IDestination,
  IDocument,
  IOptionMap,
  IPremium,
  IProduct,
  IQuotes,
  IStateCities,
  ITraveller,
  TripType
} from '../../../../data/api/models';
import { ValidationFailed } from '../Components/ValidationFailed';
import { II18nMessages } from '../../../../services/branding/models';
import { bindActionCreators } from 'redux';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import { ICollapsableComponent, IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import { AdditionalCover } from '../Components/AdditionalCover';
import { quoteHasMedical } from '../../../../helpers/signpost';
import { SchemeChoice } from '../Components/SchemeChoice';
import { FooterPriceDisplay } from '../Components/FooterPriceDisplay';
import { TermsAndConditions } from '../Components/TermsAndConditions';
import Loading from '../../../../components/Loading';
import { QuoteStepButtons } from '../Components/QuoteStepButtons';
import { CalculationError } from '../Components/CalculationError';
import { IVeriskState } from '../../../../services/verisk/models';
import { ToManyAttempts } from '../Components/ToManyAttemptsError';
import { HazardousActivities } from '../AboutTheTravellers/component/HazardousActivities';
import { scrollToTop } from '../../../../helpers/TravelApplication';
import { InfoAnnie } from '../../components/InfoAnnie';

interface IYourQuoteFormProps extends InjectedFormProps {
  auth: IAuthState;
  travellers: ITraveller[];
  tripType: TripType;
  quotes: IQuotes;
  groupType: string;
  region: string;
  premiums: IPremium[],
  schemeId: string,
  options: IOptionMap,
  destinations: IDestination[];
  stateCities: IStateCities[];
  product: IProduct;
  i18n: II18nMessages;
  synchronousError: any;
  coverLevel: string;
  channel: IChannel;
  quoteState: IQuoteState;
  destinationsValue: string[];
  stateCitiesValue: string[];
  instaquoteDecline?: boolean;
  startDate: string;
  endDate: string;
  isInstaquote: boolean;
  isDirectquoteRetrieval: boolean;
  cprResponse: any;
  verisk: IVeriskState;
  createdBeforeFixedSchemeVersionRule: boolean;
  quoteType: string;
  maxTripDuration: any;
  documents: IDocument[];
  campaignCode: string;
  onBack: () => any;
  dispatch: any;
  collapsable: ICollapsableComponent;
  selectedSchemeId: string;
  quoteForEdit: boolean;
  actions: {
    quote: IQuoteActions;
    submit(name: string);
  }

  renderDefinition(name: string, useProvidedString?: boolean): any;

  timeOfQuote?: string;
  mods?: any[];
}

class YourQuote extends React.Component<IYourQuoteFormProps, any> {

  constructor(props) {
    super(props);
    this.state = {
      showValidateModal: false,
      showDeclineModal: false,
      showGroupTypes: true,
    }
    scrollToTop();
    this.recalculate = this.recalculate.bind(this);
  }

  public componentWillMount() {
    const { quoteState, actions, change, schemeId, campaignCode, isDirectquoteRetrieval } = this.props;
    const voucherCode = quoteState.campaignCode ? quoteState.campaignCode : campaignCode;
    if (voucherCode) {
      if (!campaignCode) {
        change('campaignCode', voucherCode);
      }
      actions.quote.dispatchVoucherCode(voucherCode);
    }
    if (!schemeId) {
      this.recalculate();
    }

    if (isDirectquoteRetrieval) {
      this.recalculate();
    }
  }

  componentDidUpdate(prevProps: Readonly<IYourQuoteFormProps>, prevState: Readonly<any>, snapshot?: any) {
    const { change, quoteState, schemeId, travellers, cprResponse } = this.props;
    let selectedPremium;

    if (!schemeId && quoteState.quotes) {
      selectedPremium = this.getSelectedPremium();
      change('schemeId', get(selectedPremium, 'scheme.id'))
    }

    if (schemeId && quoteState.quotes) {
      selectedPremium = this.getSelectedPremium();
      const hasMedicalAdded = quoteHasMedical(null, travellers);
      change('signposted', {
        medicalDecline: false,
        medicalLoad: get(selectedPremium, 'isSignposted', false) && hasMedicalAdded,
      });
      change('medicalCoverForCovid', get(selectedPremium, 'information.medicalCoverForCovid', false));
    }

    if (get(quoteState, 'cprResponse') && isEmpty(cprResponse)) {
      change('cprResponse', get(quoteState, 'cprResponse'));
    }

  }

  private getSelectedPremium = (): any => {
    const { actions, selectedSchemeId, tripType } = this.props;
    return actions.quote.getQuoteSelectedPremium(selectedSchemeId, tripType);
  }

  private handleSubmitForm = () => {
    const { submit, isInstaquote } = this.props;
    if (isInstaquote) {
      this.setState({ showValidateModal: true })
    } else {
      submit();
    }
  }

  private readonly recalculate = (shouldUpdate = true) => {
    const {
      actions,
      quoteType,
      instaquoteDecline,
      change,
      isInstaquote,
      groupType,
      timeOfQuote,
      mods
    } = this.props;

    if (groupType !== 'individual') {
      this.setState({ showGroupTypes: false })
    }

    const timeOfQuoteFromPremiumInfo = get(this.getSelectedPremium(), 'information.timeOfQuote')
    const modsFromPremiumInfo = get(this.getSelectedPremium(), 'information.mods')
    const isNewBusiness = quoteType === 'new business'

    if (!timeOfQuote && timeOfQuoteFromPremiumInfo && isNewBusiness) {
      change('timeOfQuote', timeOfQuoteFromPremiumInfo);
    }

    if (!mods && mods && isNewBusiness) {
      change('mods', modsFromPremiumInfo);
    }

    change('calculateAllSchemes', true);
    Promise.resolve(actions.quote.calculate(null, true, true, shouldUpdate)).then(() => {
      if (instaquoteDecline && isInstaquote) {
        this.setState({ showDeclineModal: true })
      }
    })
  }

  private getWordingUrl = (): string => {
    const { documents } = this.props;
    let wording;

    wording = find(documents, ['name', 'Policy Wording']);
    let path = get(wording, 'filePath', null);

    if (!wording) {
      wording = get(this.getSelectedPremium(), 'scheme.documents.Policy Wording')
      if (wording) {
        path = wording;
      }
    }

    return path;
  }


  public render() {
    const {
      handleSubmit,
      valid,
      submitFailed,
      submitting,
      travellers,
      tripType,
      region,
      destinationsValue,
      onBack,
      synchronousError,
      options,
      quoteState,
      channel,
      isInstaquote,
      isDirectquoteRetrieval,
      startDate,
      endDate,
      dispatch,
      collapsable,
      actions,
      schemeId,
    } = this.props

    const { showDeclineModal } = this.state;
    const leadTraveller = head(travellers);
    const disableNext = get(options, 'hazardous_activities') === true || get(options, 'hazardous_activities', null) === null;

    if (isEmpty(leadTraveller)) {
      return (
        <Loading/>
      )
    }

    if (get(quoteState, 'toManyAttempts', false)) {
      return (
        <div className="steps-container">
          <ToManyAttempts/>
        </div>
      )
    }

    if (get(quoteState, 'calculationError.type')) {
      return (
        <div className="steps-container">
          <CalculationError calculationError={quoteState.calculationError}/>
        </div>
      )
    }

    const selectedPremium = this.getSelectedPremium();
    const allowBackButton = !isInstaquote
    return (
      <>
        <Form horizontal={true} onSubmit={handleSubmit(this.handleSubmitForm)} autoComplete="off">

          <div className="steps-container your-quote">

            {quoteState.isCalculating && (
              <>
                <Loading/><span>Calculating...</span>
              </>
            )}

            {submitFailed && !valid && (
              <ValidationFailed/>
            )}

            <InfoAnnie title={'Which level of cover do you need?'}
                       text={'We have 3 levels of cover for you to consider.'}/>

            <SchemeChoice
              tripType={tripType}
              premium={selectedPremium}
              valid={true}
              recalculate={this.recalculate}
              collapsable={collapsable}
              {...this.props}
            />

            <AdditionalCover
              quoteState={quoteState}
              recalculate={this.recalculate}
              channel={channel}
              startDate={startDate}
              endDate={endDate}
              tripType={tripType}
              valid={true}
              collapsable={collapsable}
              selectedPremium={selectedPremium}
              {...this.props}
            />

            <HazardousActivities
              options={options}
              wordingUrl={this.getWordingUrl()}
              collapsable={collapsable}
              dispatch={dispatch}
              valid={valid}
              {...this.props}
            />

            {schemeId && <FooterPriceDisplay premium={selectedPremium} tripType={tripType}/>}

            {(isInstaquote || isDirectquoteRetrieval) && (
              <div style={{ margin: '30px 0 0 0' }}>
                <TermsAndConditions/>
              </div>
            )}

            {!showDeclineModal && (
              <div style={{ margin: '30px 0 0 0' }}>
                <QuoteStepButtons
                  submitting={submitting}
                  submitFailed={submitFailed}
                  valid={valid}
                  hasBackButton={allowBackButton}
                  handleBack={onBack}
                  disableBack={quoteState.isCalculating}
                  disable={quoteState.isCalculating || disableNext || !valid}
                  submitButtonType={'validateButton'}
                  checkForErrors={true}
                  synchronousError={synchronousError}
                  submit={actions.submit}
                />
              </div>
            )}

          </div>
        </Form>
      </>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: false,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const quoteState = state.quote;
      const tripType = selector(state, 'tripType');
      const travellers = selector(state, 'travellers');
      const quotes = selector(state, 'quotes');
      const groupType = selector(state, 'groupType');
      const premiums = selector(state, 'premiums');
      const schemeId = selector(state, 'schemeId');
      const options = selector(state, 'options');
      const coverLevel = selector(state, 'coverLevel');
      const instaquoteDecline = selector(state, 'instaquoteDecline');
      const startDate = selector(state, 'startDate');
      const endDate = selector(state, 'endDate');
      const region = selector(state, 'region');
      const destinationsValue = selector(state, 'destinations');
      const stateCitiesValue = selector(state, 'stateCities');
      const cprResponse = selector(state, 'cprResponse');
      const timeOfQuote = selector(state, 'timeOfQuote');
      const mods = selector(state, 'mods');
      const campaignCode = selector(state, 'campaignCode');
      const quoteType = selector(state, 'quoteType');
      const documents = selector(state, 'documents');
      const maxTripDuration = selector(state, 'maxTripDuration');
      const selectedSchemeId = selector(state, 'selectedSchemeId');
      const synchronousError = getFormSyncErrors('quote')(state);
      return {
        auth: state.auth,
        createdBeforeFixedSchemeVersionRule: state.quote.application.createdBeforeFixedSchemeVersionRule,
        verisk: state.verisk,
        collapsable: state.quote.collapsableComponents,
        quoteForEdit: state.quote.quoteForEdit || false,
        tripType,
        travellers,
        quotes,
        groupType,
        quoteState,
        premiums,
        options,
        coverLevel,
        schemeId,
        instaquoteDecline,
        documents,
        startDate,
        endDate,
        region,
        synchronousError,
        destinationsValue,
        stateCitiesValue,
        cprResponse,
        timeOfQuote,
        mods,
        campaignCode,
        quoteType,
        maxTripDuration,
        selectedSchemeId,
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        submit: bindActionCreators(submit, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
      },
    }),
  ),
])(YourQuote);
